import { Winda } from "@brexhq/next/src/utils/Winda";
import {
  ColoredIconTypes,
  IconTypes,
} from "@brexhq/shared/components/deprecated/Icons/types";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";

import { defaultCardRenderer } from "./components/SwipeCard/SwipeCard";
import {
  DefaultCarouselCardProps,
  DESKTOP_CARD_WIDTH,
  DragDirection,
  MOBILE_CARD_WIDTH,
  SlideStyles,
  SwipeCardData,
  TransformedCarouselCardProps,
} from "./types";

export const getScrollDirection = (
  position: number,
  nextPosition: number,
): DragDirection => {
  if (nextPosition > position) return "right";
  else if (nextPosition < position) return "left";
  return "none";
};

export const calculateMaxNumberOfScrolls = ({
  slideContainerPosition,
  cardWidth,
  cardCount,
  offsetRight = 0,
}: {
  slideContainerPosition: number;
  cardWidth: number;
  cardCount: number;
  offsetRight?: number;
}) => {
  const activeArea =
    (Winda.get("innerWidth") ?? 0) - slideContainerPosition - offsetRight;
  const maxRange = activeArea / cardWidth;
  // if active area with offset is less than card width, then only 1 card will show on final scroll
  const cardsShownAtEnd = activeArea > cardWidth ? Math.floor(maxRange) : 1;

  return {
    maxScrolls: cardsShownAtEnd > 0 ? cardCount - cardsShownAtEnd : 0,
    maxRange: cardCount - maxRange,
  };
};

export const getDefaultCardStyles = (spacing: {
  m10: number;
  m16: number;
}): { width: SlideStyles; margin: SlideStyles } => {
  return {
    width: {
      LRG: DESKTOP_CARD_WIDTH,
      SML: MOBILE_CARD_WIDTH,
    },
    margin: {
      LRG: spacing.m16,
      SML: spacing.m10,
    },
  };
};

export const mapToBounds = (
  currentPos: number,
  containerWidth: number,
  sliderWidth: number,
): number => {
  const isInMinBounds = currentPos > 0;
  const isInMaxBounds = currentPos < containerWidth - sliderWidth;
  let xPos = currentPos;
  if (isInMinBounds && isInMaxBounds) {
    xPos = currentPos;
  } else if (!isInMinBounds) {
    xPos = 0;
  } else if (!isInMaxBounds) {
    xPos = containerWidth - sliderWidth;
  }

  return xPos;
};

type SanityCard = SwipeCardData;

function optional<T>(value: T) {
  return value ?? undefined;
}

export const mapCard = (
  moduleTheme: ModuleThemeType,
  alignment: DefaultCarouselCardProps["alignment"],
  sanityCard: SanityCard | undefined | null,
  index: number,
  width: SlideStyles,
  margin: SlideStyles,
  cardTheme?: ModuleThemeType,
): TransformedCarouselCardProps => {
  const card: DefaultCarouselCardProps = {
    id: `${index}`,
    width,
    margin,
    alignment,
    moduleTheme,
    primaryIcon: optional(
      sanityCard?.primaryIcon?.icon as ColoredIconTypes | null,
    ),
    secondaryIcon: optional(
      sanityCard?.secondaryIcon?.icon as IconTypes | null,
    ),
    headerPortableText: optional(sanityCard?.headerPortableText),
    bodyPortableText: optional(sanityCard?.bodyPortableText),
    image: sanityCard?.image,
    cardUrl: optional(
      [
        sanityCard?.cardUrl?.to ?? sanityCard?.cardUrl?.toExternal ?? "",
        sanityCard?.cardUrl?.hash ? `#${sanityCard?.cardUrl?.hash}` : "",
      ].join(""),
    ),
    buttonText: optional(sanityCard?.buttonText),
    cardTheme,
  };

  return transformCardToDefault(card);
};

export const transformCardToDefault = (
  card: DefaultCarouselCardProps,
): TransformedCarouselCardProps => {
  return {
    id: `${card.id}`,
    cardUrl: card.cardUrl,
    renderedCard: defaultCardRenderer(card),
    cardWidth: {
      LRG: card.width.LRG + card.margin.LRG,
      SML: card.width.SML + card.margin.SML,
    },
  };
};
